import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { message } from "antd";
import TestDirectoryService from "services/testDirectoryService";
import { formatValidationErrors } from "utils/ArrayUtils";

export const initialState = {
  list: [],
  testsAndPanels: [],
  urgent_and_stat_price: [],
  fetching: false,
  creating: false,
  updating: false,
  deleting: false,
  uploading: false,
  errorMessages: [],
  showMessage: false,
  showEditForm: false,
  showNewForm: false,
};

export const fetchTestDirectories = createAsyncThunk(
  "/fetchTestDirectories",
  async (data, { rejectWithValue }) => {
    try {
      const response = await TestDirectoryService.fetch();
      var code = response.code;
      return {
        code: code,
        tests: response.data,
      };
    } catch (err) {
      let code = err.response?.data.code;
      let response = err.response;
      return rejectWithValue({
        code: code,
        errorMessages: response?.data?.message || "Error",
      });
    }
  }
);

export const fetchTestAndPanel = createAsyncThunk(
  "/fetchTestAndPanel",
  async (data, { rejectWithValue }) => {
    try {
      const response = await TestDirectoryService.fetchTestAndPanel();
      var code = response.code;
      return {
        code: code,
        testsAndPanels: response.data,
      };
    } catch (err) {
      let code = err.response?.data.code;
      let response = err.response;
      return rejectWithValue({
        code: code,
        errorMessages: response?.data?.message || "Error",
      });
    }
  }
);

export const fetchStatAndUrgentPrice = createAsyncThunk(
  "/fetchStatAndUrgentPrice",
  async (data, { rejectWithValue }) => {
    try {
      const response = await TestDirectoryService.fetchStatAndUrgentPrice();
      var code = response.code;
      return {
        code: code,
        statAndUrgentPrice: response.data,
      };
    } catch (err) {
      let code = err.response?.data.code;
      let response = err.response;
      return rejectWithValue({
        code: code,
        errorMessages: response?.data?.message || "Error",
      });
    }
  }
);

export const createTestDirectory = createAsyncThunk(
  "/createTestDirectory",
  async (data, { rejectWithValue }) => {
    try {
      const response = await TestDirectoryService.create(data);
      const code = response.original.code;
      return {
        code: code,
        tests: response.original.data,
      };
    } catch (err) {
      return rejectWithValue(
        { code: err.response.status, error: err.response?.data.errors } || {
          error: "Error creating a test",
        }
      );
    }
  }
);

export const updateStatAndUrgentPrice = createAsyncThunk(
  "/updateStatAndUrgentPrice",
  async (data, { rejectWithValue }) => {
    try {
      const response =
        await TestDirectoryService.updateStatAndUrgentPrice(data);
      const code = response.code;
      return {
        code: code,
        statAndUrgentPrice: response.data,
      };
    } catch (err) {
      return rejectWithValue(
        { code: err.response.status, error: err.response?.data.errors } || {
          error: "Error creating a test",
        }
      );
    }
  }
);

export const updateTestDirectory = createAsyncThunk(
  "/updateTestDirectory",
  async (data, { rejectWithValue }) => {
    try {
      const response = await TestDirectoryService.update(data);
      const code = response.original.code;
      return {
        code: code,
        tests: response.original.data,
      };
    } catch (err) {
      return rejectWithValue(
        { code: err.response.status, error: err.response?.data.errors } || {
          error: "Error creating a test",
        }
      );
    }
  }
);

export const deleteTestDirectory = createAsyncThunk(
  "/deleteTestDirectory",
  async (id, { rejectWithValue }) => {
    try {
      const response = await TestDirectoryService.delete(id);
      const code = response.original.code;
      return {
        code: code,
        tests: response.original.data,
      };
    } catch (err) {
      return rejectWithValue({ error: "Error deleting a test directory" });
    }
  }
);

export const uploadTestDirectory = createAsyncThunk(
  "/uploadTestDirectory",
  async (formData, { rejectWithValue }) => {
    try {
      const response = await TestDirectoryService.upload(formData);
      const code = response.code;
      return {
        code: code,
        response: response.data,
      };
    } catch (err) {
      return rejectWithValue({ error: "Error deleting a test directory" });
    }
  }
);

export const testDirectorySlice = createSlice({
  name: "testDirectories",
  initialState,
  reducers: {
    showLoading: (state) => {
      state.fetching = !state.fetching;
    },
    toggleTestDirectoryEditForm: (state) => {
      state.showEditForm = !state.showEditForm;
    },
    toggleTestDirectoryNewForm: (state) => {
      state.showNewForm = !state.showNewForm;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTestDirectories.pending, (state) => {
        state.fetching = true;
      })
      .addCase(fetchTestDirectories.fulfilled, (state, action) => {
        state.fetching = false;
        state.list = action.payload.tests;
      })
      .addCase(fetchTestDirectories.rejected, (state, action) => {
        state.fetching = false;
      })
      .addCase(fetchStatAndUrgentPrice.pending, (state) => {})
      .addCase(fetchStatAndUrgentPrice.fulfilled, (state, action) => {
        state.urgent_and_stat_price = action.payload.statAndUrgentPrice;
      })
      .addCase(fetchStatAndUrgentPrice.rejected, (state, action) => {})
      .addCase(fetchTestAndPanel.pending, (state) => {
        state.fetching = true;
      })
      .addCase(fetchTestAndPanel.fulfilled, (state, action) => {
        state.fetching = false;
        state.testsAndPanels = action.payload.testsAndPanels;
      })
      .addCase(fetchTestAndPanel.rejected, (state, action) => {
        state.fetching = false;
      })
      .addCase(createTestDirectory.pending, (state) => {
        state.creating = true;
      })
      .addCase(createTestDirectory.fulfilled, (state, action) => {
        state.creating = false;
        state.list = action.payload.tests;
        state.showNewForm = !state.showNewForm;
      })
      .addCase(createTestDirectory.rejected, (state, action) => {
        if (action.payload.code == 422) {
          state.errorMessages = formatValidationErrors(action.payload.error);
        } else {
          // state.errorMessages = action.payload.error;
        }
        state.creating = false;
      })
      .addCase(updateTestDirectory.pending, (state, action) => {
        state.updating = true;
      })
      .addCase(updateTestDirectory.fulfilled, (state, action) => {
        state.updating = false;
        state.list = action.payload.tests;
        state.showEditForm = !state.showEditForm;
      })
      .addCase(updateTestDirectory.rejected, (state, action) => {
        if (action.payload.code == 422) {
          state.errorMessages = formatValidationErrors(action.payload.error);
        } else {
          state.errorMessages = action.payload.error;
        }
        state.updating = false;
      })
      .addCase(updateStatAndUrgentPrice.pending, (state, action) => {
        state.updating = true;
      })
      .addCase(updateStatAndUrgentPrice.fulfilled, (state, action) => {
        message.success("Updated Successfully");
        state.updating = false;
        state.urgent_and_stat_price = action.payload.statAndUrgentPrice;
      })
      .addCase(updateStatAndUrgentPrice.rejected, (state, action) => {
        state.updating = false;
        message.error("Failed to update");
      })

      .addCase(uploadTestDirectory.pending, (state, action) => {
        state.uploading = true;
      })
      .addCase(uploadTestDirectory.fulfilled, (state, action) => {
        window.location.reload();
      })
      .addCase(uploadTestDirectory.rejected, (state, action) => {
        state.uploading = false;
        message.error("Failed to upload");
      })
      .addCase(deleteTestDirectory.pending, (state, action) => {
        state.deleting = true;
      })
      .addCase(deleteTestDirectory.fulfilled, (state, action) => {
        state.deleting = false;
        state.list = action.payload.tests;
      })
      .addCase(deleteTestDirectory.rejected, (state, action) => {
        state.deleting = false;
      });
  },
});

export const {
  showLoading,
  toggleTestDirectoryEditForm,
  toggleTestDirectoryNewForm,
} = testDirectorySlice.actions;

export default testDirectorySlice.reducer;
